<template>
  <div class="new-product-cold-start">
    <ContextBar />
    <div class="ims-container">
      <FilterBar />
      <ViewTabs />
      <ConfigTable />
    </div>
    <ConfigDrawer />
    <BatchImportModal />
    <CopyToCWModal />
    <DeleteConfirmModal />
  </div>
</template>

<script>
import { toRefs, reactive, provide, onMounted } from 'vue'
import ContextBar from './components/ContextBar.vue'
import FilterBar from './components/FilterBar.vue'
import ViewTabs from './components/ViewTabs.vue'
import ConfigTable from './components/ConfigTable.vue'
import ConfigDrawer from './components/ConfigDrawer.vue'
import BatchImportModal from './components/BatchImportModal.vue'
import CopyToCWModal from './components/CopyToCWModal.vue'
import DeleteConfirmModal from './components/DeleteConfirmModal.vue'
import useAPI from 'UseApi'
import { cmc_admin_category_list_get } from '@/server/modules/609'
import {
  cmc_admin_coldStart_config_list_get,
  cmc_admin_coldStart_config_add_post,
  cmc_admin_coldStart_config_edit_post,
  cmc_admin_coldStart_config_page_post,
  cmc_admin_coldStart_config_delete_post,
  cmc_admin_coldStart_config_enable_post,
  cmc_admin_coldStart_config_batchImport_preview_post,
  cmc_admin_coldStart_config_batchImport_post
} from '@/server/modules/25187'
import { message } from 'ant-design-vue'

const { common_querySiteInfo_get_ } = useAPI(null, [3426])

export default {
  components: {
    ContextBar,
    FilterBar,
    ViewTabs,
    ConfigTable,
    ConfigDrawer,
    BatchImportModal,
    CopyToCWModal,
    DeleteConfirmModal
  },
  setup() {
    const state = reactive({
      searchParams: {
        brandId: 1,
        channelId: 1,
        siteId: 1
      },
      brands: [],
      channels: [],
      sites: [],
      onContextChange: () => {
        state.pageInfo.pageNum = 1
        state.initList()
      },
      batchImportVisible: false,
      batchImportFileList: [],
      openBatchImport: () => {
        state.batchImportFileList = []
        state.batchImportVisible = true
      },
      closeBatchImport: () => {
        state.batchImportFileList = []
        state.batchImportVisible = false
      },
      handleBatchImportChange: ({ fileList }) => {
        state.batchImportFileList = fileList.slice(-1)
      },
      confirmBatchImport: async () => {
        const file = state.batchImportFileList[0]?.originFileObj
        if (!file) return
        const {
          success,
          data,
          retInfo
        } = await cmc_admin_coldStart_config_batchImport_preview_post({
          file,
          ...state.searchParams
        })
        if (!success) {
          message.error(retInfo || '预览失败')
          return
        }
        const { successCount, successList, failList } = data || {}
        if (!successCount || successCount <= 0) {
          message.error(`导入失败，共 ${failList?.length || 0} 条错误`)
          return
        }
        const {
          success: confirmSuccess,
          retInfo: confirmRetInfo
        } = await cmc_admin_coldStart_config_batchImport_post({ successList })
        if (!confirmSuccess) {
          message.error(confirmRetInfo || '导入失败')
          return
        }
        message.success(
          `已成功导入 ${successCount} 条数据，${failList?.length || 0} 条失败`
        )
        state.closeBatchImport()
        state.initList()
      },
      downloadTemplate: () => {
        const link = document.createElement('a')
        link.href =
          'https://cdn-image.kapeixi.cn/web/5b8f687f-a007-4412-9b01-856903dda65d/new_support_temp.xlsx'
        link.download = 'new_support_temp.xlsx'
        link.click()
      },
      filterParams: {
        firstCategoryId: undefined,
        secondCategoryId: undefined,
        thirdCategoryId: undefined
      },
      categoryTree: [],
      category1List: [],
      category2List: [],
      category3List: [],
      getCategoryList: async () => {
        const { success, data, retInfo } = await cmc_admin_category_list_get()
        if (success) {
          state.categoryTree = data || []
          state.category1List = data.map(item => ({
            id: item.categoryId,
            name: item.categoryName
          }))
        } else {
          message.error(retInfo)
        }
      },
      onCategory1Change: () => {
        state.filterParams.secondCategoryId = undefined
        state.filterParams.thirdCategoryId = undefined
        state.category3List = []
        const category1 = state.categoryTree.find(
          item => item.categoryId === state.filterParams.firstCategoryId
        )
        state.category2List = (category1?.subCategories || []).map(item => ({
          id: item.categoryId,
          name: item.categoryName
        }))
      },
      onCategory2Change: () => {
        state.filterParams.thirdCategoryId = undefined
        const category1 = state.categoryTree.find(
          item => item.categoryId === state.filterParams.firstCategoryId
        )
        const category2 = (category1?.subCategories || []).find(
          item => item.categoryId === state.filterParams.secondCategoryId
        )
        state.category3List = (category2?.subCategories || []).map(item => ({
          id: item.categoryId,
          name: item.categoryName
        }))
      },
      handleSearch: () => {
        state.pageInfo.pageNum = 1
        state.initList()
      },
      handleResetFilter: () => {
        state.filterParams = {
          firstCategoryId: undefined,
          secondCategoryId: undefined,
          thirdCategoryId: undefined
        }
        state.category2List = []
        state.category3List = []
        state.pageInfo.pageNum = 1
        state.initList()
      },
      toggleStatus: async record => {
        const {
          success,
          retInfo
        } = await cmc_admin_coldStart_config_enable_post({
          configId: record.configId,
          supportStatus: record.supportStatus === 1 ? 0 : 1
        })
        if (!success) {
          message.error(retInfo || '操作失败')
          return
        }
        state.initList()
      },
      handleCopyToCW: record => {
        state.openEditDrawer(record)
        state.configModalMode = 'create'
        state.editingRecord = null
        const category1 = state.categoryTree.find(
          item => item.categoryId === record.firstCategoryId
        )
        state.configFormCategory2List = (
          category1?.subCategories || []
        ).map(item => ({ id: item.categoryId, name: item.categoryName }))
        const category2 = (category1?.subCategories || []).find(
          item => item.categoryId === record.secondCategoryId
        )
        state.configFormCategory3List = (
          category2?.subCategories || []
        ).map(item => ({ id: item.categoryId, name: item.categoryName }))
      },
      handleEdit: record => {
        state.openEditDrawer(record)
      },
      deleteModalVisible: false,
      deleteRecord: null,
      openDeleteModal: record => {
        state.deleteRecord = record
        state.deleteModalVisible = true
      },
      closeDeleteModal: () => {
        state.deleteModalVisible = false
        state.deleteRecord = null
      },
      confirmDelete: async () => {
        if (!state.deleteRecord) return
        const {
          success,
          retInfo
        } = await cmc_admin_coldStart_config_delete_post({
          configId: state.deleteRecord.configId
        })
        if (!success) {
          message.error(retInfo || '删除失败')
          return
        }
        message.success('删除成功')
        state.deleteModalVisible = false
        state.deleteRecord = null
        state.initList()
      },
      configModalVisible: false,
      configModalMode: 'create',
      editingRecord: null,
      editCategoryParts: [],
      configForm: {
        firstCategoryId: undefined,
        secondCategoryId: undefined,
        thirdCategoryId: undefined,
        selectedYear: undefined,
        startCW: undefined,
        endCW: undefined,
        uvTarget: undefined,
        weeklyOverrides: {}
      },
      configFormCategory2List: [],
      configFormCategory3List: [],
      weeklyAdjustExpanded: false,
      weeksList: [],
      yearOptions: [],
      cwOptions: [],
      configFormCWRange: [],
      openConfigDrawer: () => {
        state.configModalMode = 'create'
        state.editingRecord = null
        state.editCategoryParts = []
        state.configForm = {
          firstCategoryId: undefined,
          secondCategoryId: undefined,
          thirdCategoryId: undefined,
          selectedYear: state.currentYear || undefined,
          startCW: undefined,
          endCW: undefined,
          uvTarget: undefined,
          weeklyOverrides: {}
        }
        state.configFormCategory2List = []
        state.configFormCategory3List = []
        state.weeklyAdjustExpanded = false
        state.configFormCWRange = []
        state.cwOptions = state.weeksList
          .filter(w => w.year === state.currentYear)
          .map(w => w.cwDisplay)
        state.configModalVisible = true
      },
      openEditDrawer: record => {
        if (!record) return
        state.configModalMode = 'edit'
        state.editingRecord = record
        state.editCategoryParts = (record.categoryPath || '')
          .split('>')
          .map(s => s.trim())
          .filter(Boolean)

        const details = record.weekConfigDetails || []
        const firstWeekItem = details.length
          ? state.weeksList.find(w => w.weekCode === details[0].weekCode)
          : null
        const lastWeekItem = details.length
          ? state.weeksList.find(
              w => w.weekCode === details[details.length - 1].weekCode
            )
          : null

        const selectedYear = firstWeekItem?.year || state.currentYear
        const startCW = firstWeekItem?.cwDisplay
        const endCW = lastWeekItem?.cwDisplay

        state.cwOptions = state.weeksList
          .filter(w => w.year === selectedYear)
          .map(w => w.cwDisplay)

        const uvTarget =
          record.uvTarget ??
          record.uvBase ??
          record.uvTargetValue ??
          (details[0] ? details[0].dayUvMax : undefined)

        const baseForm = {
          firstCategoryId: record.firstCategoryId,
          secondCategoryId: record.secondCategoryId,
          thirdCategoryId: record.thirdCategoryId,
          selectedYear,
          startCW,
          endCW,
          uvTarget,
          weeklyOverrides: {}
        }

        state.configForm = baseForm
        state.configFormCategory2List = []
        state.configFormCategory3List = []
        state.updateCWRange()

        const overrides = {}
        details.forEach(w => {
          const weekItem = state.weeksList.find(
            wk => wk.weekCode === w.weekCode
          )
          if (weekItem && (w.dayUvMax === 0 || w.dayUvMax)) {
            overrides[weekItem.cwDisplay] = w.dayUvMax
          }
        })
        state.configForm.weeklyOverrides = overrides
        state.weeklyAdjustExpanded = Object.keys(overrides).length > 0
        state.configModalVisible = true
      },
      closeConfigModal: () => {
        state.configModalVisible = false
        state.configModalMode = 'create'
        state.editingRecord = null
        state.editCategoryParts = []
      },
      onConfigCategory1Change: () => {
        state.configForm.secondCategoryId = undefined
        state.configForm.thirdCategoryId = undefined
        state.configFormCategory3List = []
        if (!state.configForm.firstCategoryId) {
          state.configForm.startCW = undefined
          state.configForm.endCW = undefined
          state.configForm.uvTarget = undefined
          state.configForm.weeklyOverrides = {}
          state.configFormCWRange = []
          state.weeklyAdjustExpanded = false
        }
        const category1 = state.categoryTree.find(
          item => item.categoryId === state.configForm.firstCategoryId
        )
        state.configFormCategory2List = (category1?.subCategories || []).map(
          item => ({
            id: item.categoryId,
            name: item.categoryName
          })
        )
      },
      onConfigCategory2Change: () => {
        state.configForm.thirdCategoryId = undefined
        const category1 = state.categoryTree.find(
          item => item.categoryId === state.configForm.firstCategoryId
        )
        const category2 = (category1?.subCategories || []).find(
          item => item.categoryId === state.configForm.secondCategoryId
        )
        state.configFormCategory3List = (category2?.subCategories || []).map(
          item => ({
            id: item.categoryId,
            name: item.categoryName
          })
        )
      },
      updateCWRange: () => {
        const { startCW, endCW } = state.configForm
        if (!startCW || !endCW) {
          state.configFormCWRange = []
          return
        }
        const startIdx = state.cwOptions.indexOf(startCW)
        const endIdx = state.cwOptions.indexOf(endCW)
        if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) {
          state.configFormCWRange = []
          return
        }
        state.configFormCWRange = state.cwOptions.slice(startIdx, endIdx + 1)
      },
      onYearChange: () => {
        state.configForm.startCW = undefined
        state.configForm.endCW = undefined
        state.configFormCWRange = []
        state.cwOptions = state.weeksList
          .filter(w => w.year === state.configForm.selectedYear)
          .map(w => w.cwDisplay)
      },
      onStartCWChange: () => {
        if (
          state.configForm.endCW &&
          state.cwOptions.indexOf(state.configForm.endCW) <
            state.cwOptions.indexOf(state.configForm.startCW)
        ) {
          state.configForm.endCW = undefined
        }
        state.updateCWRange()
      },
      onEndCWChange: () => {
        state.updateCWRange()
      },
      toggleWeeklyAdjust: () => {
        state.weeklyAdjustExpanded = !state.weeklyAdjustExpanded
      },
      buildWeekConfigs: (cwRange, weeklyOverrides, uvTarget, selectedYear) => {
        return cwRange.map(cw => {
          const weekItem = state.weeksList.find(
            w => w.cwDisplay === cw && w.year === selectedYear
          )
          const override = weeklyOverrides[cw]
          const dayUvMax = override === 0 || override ? override : uvTarget
          return {
            weekCode: weekItem?.weekCode || cw,
            dayUvMax
          }
        })
      },
      saveConfig: async (supportStatus = 0) => {
        const {
          firstCategoryId,
          secondCategoryId,
          thirdCategoryId,
          selectedYear,
          startCW,
          endCW,
          uvTarget,
          weeklyOverrides
        } = state.configForm
        if (!firstCategoryId) {
          message.error('请选择一级品类')
          return
        }
        if (!selectedYear) {
          message.error('请选择年份')
          return
        }
        if (!startCW) {
          message.error('请选择起始CW')
          return
        }
        if (!endCW) {
          message.error('请选择结束CW')
          return
        }
        if (!uvTarget) {
          message.error('请输入UV基准值')
          return
        }
        const weekConfigs = state.buildWeekConfigs(
          state.configFormCWRange,
          weeklyOverrides,
          uvTarget,
          selectedYear
        )
        const params = {
          brandId: state.searchParams.brandId,
          channelId: state.searchParams.channelId,
          siteId: state.searchParams.siteId,
          firstCategoryId,
          secondCategoryId,
          thirdCategoryId,
          supportStatus,
          weekConfigs
        }
        const { success, retInfo } = await cmc_admin_coldStart_config_add_post(
          params
        )
        if (!success) {
          message.error(retInfo || '创建失败')
          return
        }
        message.success('配置创建成功')
        state.configModalVisible = false
        state.initList()
      },
      updateConfig: async () => {
        if (!state.editingRecord) return
        const {
          selectedYear,
          startCW,
          endCW,
          uvTarget,
          weeklyOverrides
        } = state.configForm
        if (!startCW) {
          message.error('请选择起始CW')
          return
        }
        if (!endCW) {
          message.error('请选择结束CW')
          return
        }
        if (!uvTarget) {
          message.error('请输入UV基准值')
          return
        }
        const weekConfigs = state.buildWeekConfigs(
          state.configFormCWRange,
          weeklyOverrides,
          uvTarget,
          selectedYear
        )
        const params = {
          configId: state.editingRecord.configId,
          brandId: state.searchParams.brandId,
          channelId: state.searchParams.channelId,
          siteId: state.searchParams.siteId,
          firstCategoryId: state.editingRecord.firstCategoryId,
          secondCategoryId: state.editingRecord.secondCategoryId,
          thirdCategoryId: state.editingRecord.thirdCategoryId,
          supportStatus: state.editingRecord.supportStatus ?? 0,
          weekConfigs
        }
        const { success, retInfo } = await cmc_admin_coldStart_config_edit_post(
          params
        )
        if (!success) {
          message.error(retInfo || '更新失败')
          return
        }
        message.success('配置更新成功')
        state.closeConfigModal()
        state.initList()
      },
      pageInfo: {
        pageSize: 10,
        pageNum: 1,
        total: 0,
        pages: 1
      },
      onPageChange: (page, pageSize) => {
        if (pageSize !== state.pageInfo.pageSize) {
          state.pageInfo.pageNum = 1
        } else {
          state.pageInfo.pageNum = page
        }
        state.pageInfo.pageSize = pageSize
        state.initList()
      },
      viewMode: 'rolling',
      activeQuarter: 1,
      rollingOffset: 0,
      currentYear: '',
      dataSource: [],
      currentCW: '',
      cwColumns: [],
      switchView: (mode, quarter) => {
        state.viewMode = mode
        if (mode === 'rolling') {
          state.rollingOffset = 0
        }
        if (mode === 'quarter' && quarter) {
          state.activeQuarter = quarter
        }
        state.updateCWColumns()
        state.initList()
      },
      prevQuarter: () => {
        if (state.viewMode === 'rolling') {
          const currentYearWeeks = state.weeksList.filter(
            w => w.year === state.currentYear
          )
          const currentWeekItem = currentYearWeeks.find(w => w.currentWeek)
          const currentWeekNum = currentWeekItem
            ? currentYearWeeks.indexOf(currentWeekItem) + 1
            : 1
          if (currentWeekNum + state.rollingOffset - 1 >= 1) {
            state.rollingOffset--
            state.updateCWColumns()
            state.initList()
          }
        }
      },
      nextQuarter: () => {
        if (state.viewMode === 'rolling') {
          const currentYearWeeks = state.weeksList.filter(
            w => w.year === state.currentYear
          )
          const currentWeekItem = currentYearWeeks.find(w => w.currentWeek)
          const currentWeekNum = currentWeekItem
            ? currentYearWeeks.indexOf(currentWeekItem) + 1
            : 1
          if (
            currentWeekNum + state.rollingOffset + 4 <=
            currentYearWeeks.length
          ) {
            state.rollingOffset++
            state.updateCWColumns()
            state.initList()
          }
        }
      },
      updateCWColumns: () => {
        const currentYearWeeks = state.weeksList.filter(
          w => w.year === state.currentYear
        )

        const currentWeekItem = state.weeksList.find(w => w.currentWeek)
        const currentWeekIdx = currentWeekItem
          ? currentYearWeeks.findIndex(
              w => w.weekCode === currentWeekItem.weekCode
            )
          : 0
        const currentWeekNum = currentWeekIdx + 1

        if (state.currentCW) {
          // keep currentCW as weekCode (already set in getWeeks)
        } else if (currentWeekItem) {
          state.currentCW = currentWeekItem.weekCode
        }

        if (state.viewMode === 'rolling') {
          const startIdx = currentWeekNum - 1 + state.rollingOffset
          state.cwColumns = Array.from({ length: 4 }, (_, i) => {
            const week = currentYearWeeks[startIdx + i]
            return week ? week.weekCode : null
          }).filter(Boolean)
        } else {
          const quarter = state.activeQuarter
          state.cwColumns = currentYearWeeks
            .filter((_, idx) => {
              const weekNum = idx + 1
              return (
                weekNum >= (quarter - 1) * 13 + 1 &&
                weekNum <= Math.min(quarter * 13, currentYearWeeks.length)
              )
            })
            .map(w => w.weekCode)
        }
      },
      getWeeks: async () => {
        const { success, data } = await cmc_admin_coldStart_config_list_get()
        if (success && data) {
          state.weeksList = data.weeks || []
          state.currentYear = data.currentYear || new Date().getFullYear()
          const years = [...new Set(state.weeksList.map(w => w.year))]
          state.yearOptions = years.sort((a, b) => a - b)
          const currentYearWeeks = state.weeksList.filter(
            w => w.year === state.currentYear
          )
          state.cwOptions = currentYearWeeks.map(w => w.cwDisplay)
          const currentWeekItem = state.weeksList.find(w => w.currentWeek)
          if (currentWeekItem) {
            state.currentCW = currentWeekItem.weekCode
          }
        }
      },
      initList: async () => {
        if (state.cwColumns.length === 0) {
          state.updateCWColumns()
        }
        const params = {
          brandId: state.searchParams.brandId,
          channelId: state.searchParams.channelId,
          siteId: state.searchParams.siteId,
          firstCategoryId: state.filterParams.firstCategoryId,
          secondCategoryId: state.filterParams.secondCategoryId,
          thirdCategoryId: state.filterParams.thirdCategoryId,
          weekCodes: state.cwColumns,
          pageNum: state.pageInfo.pageNum,
          pageSize: state.pageInfo.pageSize
        }
        const {
          success,
          data,
          retInfo
        } = await cmc_admin_coldStart_config_page_post(params)
        if (!success) {
          message.error(retInfo || '获取列表失败')
          return
        }
        state.pageInfo.total = data?.total || 0
        state.pageInfo.pages = data?.pages || 1
        state.dataSource = (data?.list || []).map(item => {
          const weeklyData = {}
          const categoryPath = [
            item.firstCategoryName,
            item.secondCategoryName,
            item.thirdCategoryName
          ]
            .filter(Boolean)
            .join(' > ')
          ;(item.weekConfigDetails || []).forEach(w => {
            weeklyData[w.weekCode] = w.dayUvMax
          })

          return {
            ...item,
            categoryPath,
            weeklyData
          }
        })
      }
    })

    onMounted(async () => {
      common_querySiteInfo_get_({ state })
      state.getCategoryList()
      await state.getWeeks()
      state.initList()
    })

    provide('initState', state)
    return {
      ...toRefs(state)
    }
  }
}
</script>
